import React, { useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import { withPrefix } from 'gatsby';
import styled from '@emotion/styled';

import { ThemeProvider, Link } from '$components';
import { useLocale } from '../i18n/LocaleContext';
import { locales, defaultLocale } from '../i18n/locales';
import config from '../../config';

const SCALAR_CDN =
  'https://cdn.jsdelivr.net/npm/@scalar/api-reference@1.62.5/dist/browser/standalone.js';

// The whole page is dark: header (brand navy) -> intro band -> Scalar (dark).
const Page = styled('div')`
  background: #0d0d1f;
`;

const Intro = styled('div')`
  padding: 2.5rem 5vw 1.5rem;
  background: #0d0d1f;
  color: #c9c9cf;

  h1 {
    margin: 0 0 0.75rem;
    color: #ffffff;
    font-size: 2rem;
  }

  p {
    max-width: 900px;
    margin: 0.5rem 0 0;
    line-height: 1.6;
  }

  a {
    color: #8ab4ff;
    text-decoration: underline;
  }
`;

// Scalar is rendered inside an iframe so its global side effects (it sets a
// dark <body> class and injects app-wide styles) stay contained and cannot
// leak onto other docs pages during client-side navigation.
const Frame = styled('iframe')`
  display: block;
  width: 100%;
  border: 0;
  min-height: 560px;
`;

const ApiReference = ({ location, pageContext }) => {
  const { t } = useLocale();
  const frameRef = useRef(null);
  const locale = (pageContext && pageContext.locale) || defaultLocale;
  const localeConfig = locales[locale] || locales[defaultLocale];
  const prefix = localeConfig.isDefault ? '' : `/${localeConfig.slug}`;
  const base =
    config.gatsby.siteUrl + (config.gatsby.pathPrefix !== '/' ? config.gatsby.pathPrefix : '');
  const canonical = `${base}${prefix}/api-reference`;

  const specUrl = withPrefix('/openapi.yaml');
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;height:100%}</style></head><body><div id="app"></div><script src="${SCALAR_CDN}"></script><script>Scalar.createApiReference('#app',{url:'${specUrl}'})</script></body></html>`;

  // Size the iframe to fill from its top to the bottom of the viewport, so
  // Scalar scrolls internally and there is a single, natural scroll region.
  useEffect(() => {
    const resize = () => {
      const el = frameRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      el.style.height = `${Math.max(560, window.innerHeight - top)}px`;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  return (
    <ThemeProvider location={location} locale={locale}>
      <Helmet htmlAttributes={{ lang: localeConfig.hreflang }}>
        <title>{t('apiReference.metaTitle')}</title>
        <meta name="title" content={t('apiReference.metaTitle')} />
        <meta name="description" content={t('apiReference.intro')} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={t('apiReference.metaTitle')} />
        <meta property="og:description" content={t('apiReference.intro')} />
        <meta property="twitter:title" content={t('apiReference.metaTitle')} />
        <meta property="twitter:description" content={t('apiReference.intro')} />
        <link rel="canonical" href={canonical} />
        {Object.entries(locales).map(([code, conf]) => {
          const altPrefix = conf.isDefault ? '' : `/${conf.slug}`;
          return (
            <link
              key={code}
              rel="alternate"
              hreflang={conf.hreflang}
              href={`${base}${altPrefix}/api-reference`}
            />
          );
        })}
        <link rel="alternate" hreflang="x-default" href={`${base}/api-reference`} />
      </Helmet>

      <Page>
        <Intro>
          <h1>{t('apiReference.heading')}</h1>
          <p>{t('apiReference.intro')}</p>
          <p>
            {t('apiReference.authNote')}{' '}
            <Link to="/integrations/api-keys">{t('apiReference.authLink')}</Link>.
          </p>
        </Intro>

        <Frame ref={frameRef} title={t('apiReference.metaTitle')} srcDoc={srcDoc} />
      </Page>
    </ThemeProvider>
  );
};

export default ApiReference;
